import React from "react";
import {
  Box,
  HStack,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  useColorModeValue,
} from "@chakra-ui/react";

const CANDLESTICK_HEIGHTS = [65, 40, 80, 55, 90, 45, 70, 85, 50, 75, 60, 95, 42, 68, 78, 52, 88, 46];

export default function HomeSkeleton() {
  const cardBg = useColorModeValue("white", "gray.700");
  const headerBg = useColorModeValue("gray.200", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const shimmerStart = useColorModeValue("gray.100", "gray.700");
  const shimmerEnd = useColorModeValue("blue.50", "gray.600");
  const chartStart = useColorModeValue("green.50", "green.900");
  const chartEnd = useColorModeValue("blue.50", "blue.900");

  const skeletonProps = { startColor: shimmerStart, endColor: shimmerEnd, speed: 1.2 };
  const chartSkeletonProps = { startColor: chartStart, endColor: chartEnd, speed: 1.2 };

  return (
    <>
      {/* Filter Bar */}
      <HStack spacing={3} flexWrap="wrap">
        <Skeleton height="32px" width="100px" borderRadius="md" {...skeletonProps} />
        <Skeleton height="24px" width="70px" borderRadius="full" {...skeletonProps} />
        <Skeleton height="24px" width="55px" borderRadius="full" {...skeletonProps} />
      </HStack>

      {/* Hero Stats — 4 Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} bg={cardBg} rounded="lg" px={4} py={3} boxShadow="sm">
            <Skeleton height="10px" width="70px" mb={2} {...skeletonProps} />
            <Skeleton height="22px" width="100px" mb={1} {...skeletonProps} />
            <Skeleton height="10px" width="50px" {...skeletonProps} />
          </Box>
        ))}
      </SimpleGrid>

      {/* Accordion Header 1 — Detailed Statistics (collapsed) */}
      <Box bg={cardBg} rounded="lg" px={4} py={3} boxShadow="sm">
        <Skeleton height="16px" width="150px" {...skeletonProps} />
      </Box>

      {/* Accordion Header 2 — Charts (expanded) */}
      <Box bg={cardBg} rounded="lg" px={4} py={3} boxShadow="sm">
        <Skeleton height="16px" width="80px" {...skeletonProps} />
      </Box>

      {/* Charts Section Content */}
      <Box>
        {/* 3 Pie Charts */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={4}>
          {[0, 1, 2].map((i) => (
            <Box key={i} bg={cardBg} rounded="lg" p={4} boxShadow="sm" display="flex" flexDirection="column" alignItems="center">
              <Skeleton height="12px" width="140px" mb={3} alignSelf="flex-start" {...skeletonProps} />
              <SkeletonCircle size="180px" {...skeletonProps} />
            </Box>
          ))}
        </SimpleGrid>

        {/* Area Chart with Candlestick Bars */}
        <Box bg={cardBg} rounded="lg" p={4} boxShadow="sm">
          <HStack justifyContent="space-between" mb={3}>
            <Skeleton height="12px" width="120px" {...skeletonProps} />
            <HStack spacing={1}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height="24px" width="42px" borderRadius="md" {...skeletonProps} />
              ))}
            </HStack>
          </HStack>
          {/* Candlestick-style bars */}
          <Box className="candlestick-skeleton-row">
            {CANDLESTICK_HEIGHTS.map((h, i) => (
              <Skeleton
                key={i}
                height={`${h}%`}
                borderRadius="2px"
                {...chartSkeletonProps}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Accordion Header 3 — Open Trades (expanded) */}
      <Box bg={cardBg} rounded="lg" px={4} py={3} boxShadow="sm">
        <Skeleton height="16px" width="110px" {...skeletonProps} />
      </Box>

      {/* Open Trades Section Content */}
      <Box bg={cardBg} rounded="lg" p={4} boxShadow="sm">
        {/* Action Buttons */}
        <HStack spacing={2} mb={3}>
          <Skeleton height="30px" width="80px" borderRadius="md" {...skeletonProps} />
          <Skeleton height="30px" width="60px" borderRadius="md" {...skeletonProps} />
          <Skeleton height="30px" width="60px" borderRadius="md" {...skeletonProps} />
        </HStack>

        {/* Table */}
        <Box borderWidth="1px" borderColor={borderColor} rounded="lg" overflow="hidden">
          {/* Table Header */}
          <HStack bg={headerBg} px={4} py={2} spacing={6}>
            {[80, 90, 70, 60, 70, 70].map((w, i) => (
              <Skeleton key={i} height="12px" width={`${w}px`} {...skeletonProps} />
            ))}
          </HStack>
          {/* Table Rows */}
          {[0, 1, 2, 3, 4].map((row) => (
            <HStack key={row} px={4} py={3} spacing={6} borderTopWidth="1px" borderColor={borderColor}>
              {[65, 75, 55, 50, 60, 55].map((w, col) => (
                <Skeleton key={col} height="12px" width={`${w}px`} {...skeletonProps} />
              ))}
            </HStack>
          ))}
        </Box>

        {/* Pagination */}
        <HStack spacing={3} pt={3} justifyContent="flex-end">
          <Skeleton height="24px" width="90px" borderRadius="md" {...skeletonProps} />
          <Skeleton height="24px" width="120px" borderRadius="md" {...skeletonProps} />
        </HStack>
      </Box>
    </>
  );
}
